import { useState, useEffect } from 'react';

export default function ChatRoomTour({ isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const steps = [
    {
      title: 'Welcome to QuickMeet Chat! 💬',
      description: 'This is a secure, peer-to-peer chat room. Your messages are encrypted and sent directly to other participants - never stored on our servers!',
      target: null,
      position: 'center'
    },
    {
      title: 'Participants Panel',
      description: 'See who\'s currently in the chat room. The green dot means they\'re connected and ready to chat!',
      target: '[data-tour="participants"]',
      position: 'left'
    },
    {
      title: 'End-to-End Encryption',
      description: 'This indicator shows your connection is secure. All messages are encrypted peer-to-peer for maximum privacy.',
      target: '[data-tour="encryption"]',
      position: 'bottom'
    },
    {
      title: 'Share the Room',
      description: 'Click "Copy Link" to invite others to join your chat room. They\'ll be able to join instantly with the link!',
      target: '[data-tour="copy-link"]',
      position: 'bottom'
    },
    {
      title: 'Send Messages',
      description: 'Type your message here and hit Send or press Enter. Messages are delivered instantly to all participants!',
      target: '[data-tour="message-input"]',
      position: 'top'
    },
    {
      title: 'You\'re Ready to Chat! 🎉',
      description: 'That\'s it! Share the link, wait for participants to join, and start chatting securely. You can restart this tour anytime from the help button.',
      target: null,
      position: 'center'
    }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('quickmeet_chatroom_tour_completed', 'true');
    setCurrentStep(0);
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('quickmeet_chatroom_tour_completed', 'true');
    setCurrentStep(0);
    onClose();
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const targetElement = step.target ? document.querySelector(step.target) : null;
  const targetRect = targetElement?.getBoundingClientRect();

  let tooltipStyle = {};
  let arrowClass = '';

  if (step.position === 'center' || !targetRect) {
    tooltipStyle = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  } else if (step.position === 'bottom') {
    tooltipStyle = {
      position: 'fixed',
      top: `${targetRect.bottom + 20}px`,
      left: `${targetRect.left + targetRect.width / 2}px`,
      transform: 'translateX(-50%)',
    };
    arrowClass = 'top-arrow';
  } else if (step.position === 'top') {
    tooltipStyle = {
      position: 'fixed',
      top: `${targetRect.top - 20}px`,
      left: `${targetRect.left + targetRect.width / 2}px`,
      transform: 'translate(-50%, -100%)',
    };
    arrowClass = 'bottom-arrow';
  } else if (step.position === 'left') {
    tooltipStyle = {
      position: 'fixed',
      top: `${targetRect.top + targetRect.height / 2}px`,
      left: `${targetRect.left - 20}px`,
      transform: 'translate(-100%, -50%)',
    };
    arrowClass = 'right-arrow';
  }

  return (
    <>
      <div className="fixed inset-0 z-[200] pointer-events-none">
        <svg className="absolute inset-0 w-full h-full pointer-events-auto" style={{ zIndex: 200 }}>
          <defs>
            <mask id="spotlight-mask">
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {targetRect && (
                <rect
                  x={targetRect.left - 8}
                  y={targetRect.top - 8}
                  width={targetRect.width + 16}
                  height={targetRect.height + 16}
                  rx="12"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill="rgba(0, 0, 0, 0.7)"
            mask="url(#spotlight-mask)"
          />
        </svg>

        {targetRect && (
          <div
            className="absolute rounded-xl border-4 border-[#06c] animate-pulse pointer-events-none"
            style={{
              left: `${targetRect.left - 8}px`,
              top: `${targetRect.top - 8}px`,
              width: `${targetRect.width + 16}px`,
              height: `${targetRect.height + 16}px`,
              boxShadow: '0 0 0 4px rgba(0, 102, 204, 0.2), 0 0 20px rgba(0, 102, 204, 0.4)',
            }}
          />
        )}
      </div>

      <div
        className="fixed z-[201] pointer-events-auto"
        style={tooltipStyle}
      >
        <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl p-6 max-w-md w-[90vw] sm:w-96 shadow-2xl border border-[#d2d2d7] dark:border-white/10 transition-colors relative">
          {arrowClass === 'top-arrow' && (
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-[#1c1c1e] border-l border-t border-[#d2d2d7] dark:border-white/10 rotate-45" />
          )}
          {arrowClass === 'bottom-arrow' && (
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-[#1c1c1e] border-r border-b border-[#d2d2d7] dark:border-white/10 rotate-45" />
          )}
          {arrowClass === 'right-arrow' && (
            <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-4 h-4 bg-white dark:bg-[#1c1c1e] border-r border-t border-[#d2d2d7] dark:border-white/10 rotate-45" />
          )}

          <div className="flex gap-1.5 mb-4">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 flex-1 rounded-full transition-all ${
                  index <= currentStep
                    ? 'bg-gradient-to-r from-[#06c] to-[#0055d4]'
                    : 'bg-[#d2d2d7] dark:bg-[#2c2c2e]'
                }`}
              />
            ))}
          </div>

          <h3 className="text-xl font-semibold text-[#1d1d1f] dark:text-white mb-2 transition-colors">
            {step.title}
          </h3>
          <p className="text-[#6e6e73] dark:text-[#8e8e93] leading-relaxed mb-6 transition-colors">
            {step.description}
          </p>

          <div className="flex items-center justify-between gap-3">
            <button
              onClick={handleSkip}
              className="px-4 py-2 text-sm text-[#86868b] dark:text-[#8e8e93] hover:text-[#1d1d1f] dark:hover:text-white transition-colors"
            >
              Skip Tour
            </button>

            <div className="flex gap-2">
              {currentStep > 0 && (
                <button
                  onClick={handlePrev}
                  className="px-4 py-2 rounded-full font-medium text-[#06c] hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] transition-all"
                >
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="px-6 py-2 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white rounded-full font-medium hover:shadow-lg hover:scale-105 transition-all"
              >
                {currentStep === steps.length - 1 ? 'Finish' : 'Next'}
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-[#86868b] dark:text-[#8e8e93] mt-3 transition-colors">
            Step {currentStep + 1} of {steps.length}
          </p>
        </div>
      </div>
    </>
  );
}
